// XDIV 0b share affordance (marketing brief 2026-09-03, Owner-approved):
// a rendered share image of the ratio readout -- never a raw screenshot --
// that ALWAYS carries the wordmark, bitclock.net, and a UTC capture
// timestamp (COMPLIANCE_GUARDRAILS #4: a value shown in public must be
// dated, never presented as live). The image renders exactly the strings
// the viewer's own tier already sees on screen ("Subscribers Only" stays
// "Subscribers Only"), so it never widens what free users can see.
//
// Distribution is the OS share sheet (Web Share API, files) where the
// browser supports it; otherwise the PNG downloads. No SDKs, no tracking.

export interface ShareSnapshot {
  mainRatio: string;
  hodRatio: string;
  hodPrice: string;
  lodRatio: string;
  lodPrice: string;
  btcPrice: string;
  bidDepth: string;
  askDepth: string;
  scale: "RAW" | "NORMALIZED";
  bandWidthUsd: number | null;
}

export const NAVY = "#0a0e27";
export const GREEN = "#00ff88";
export const RED = "#ff4444";
export const BLUE = "#4a9eff";
export const MONO = '"Courier New", Courier, monospace';
export const WIDTH = 1200;
export const HEIGHT = 630;

/** "2026-09-06 16:20:15 UTC" -- the self-dating caption baked into every image. */
export function formatCaptureUtc(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ` +
    `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`
  );
}

export function footerLine(snapshot: ShareSnapshot, capturedAt: Date): string {
  const band =
    snapshot.bandWidthUsd !== null && Number.isFinite(snapshot.bandWidthUsd)
      ? ` · ±$${snapshot.bandWidthUsd.toLocaleString("en-US")} band`
      : "";
  return `Captured ${formatCaptureUtc(capturedAt)}${band} · ${snapshot.scale}`;
}

/** Minimal 2D-context surface the renderer needs -- lets the layout be
 * driven by a real CanvasRenderingContext2D in the browser and by a
 * recording stub in node tests. */
export interface DrawContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  fillRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  drawImage(image: CanvasImageSource, dx: number, dy: number, dw: number, dh: number): void;
}

const LOCKED_TEXT = "Subscribers Only";

/** A paywalled slot renders like the on-screen `.locked-value` style --
 * small, dimmed, never in the live green/red numeric treatment. */
function drawValue(ctx: DrawContext, text: string, x: number, y: number, color: string): void {
  if (text === LOCKED_TEXT) {
    ctx.font = `22px ${MONO}`;
    ctx.fillStyle = "#7c8598";
  } else {
    ctx.font = `bold 44px ${MONO}`;
    ctx.fillStyle = color;
  }
  ctx.fillText(text, x, y);
}

export function drawShareImage(
  ctx: DrawContext,
  snapshot: ShareSnapshot,
  capturedAt: Date,
  logo: CanvasImageSource | null
): void {
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = NAVY;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // Wordmark (brand kit Concept A: BIT:CLOCK, monospace, green with a white divider).
  let x = 56;
  if (logo) {
    ctx.drawImage(logo, x, 44, 56, 56);
    x += 72;
  }
  ctx.textAlign = "left";
  ctx.font = `bold 40px ${MONO}`;
  ctx.fillStyle = GREEN;
  ctx.fillText("BIT", x, 86);
  x += ctx.measureText("BIT").width;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(":", x, 86);
  x += ctx.measureText(":").width;
  ctx.fillStyle = GREEN;
  ctx.fillText("CLOCK", x, 86);

  ctx.textAlign = "right";
  ctx.font = `28px ${MONO}`;
  ctx.fillStyle = "#8a93a5";
  ctx.fillText("bitclock.net", WIDTH - 56, 84);

  // Main reading.
  ctx.textAlign = "center";
  ctx.font = `600 22px ${MONO}`;
  ctx.fillStyle = BLUE;
  ctx.fillText("B I T C O I N   O R D E R   B O O K   R A T I O", WIDTH / 2, 170);
  ctx.font = `bold 190px ${MONO}`;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(snapshot.mainRatio, WIDTH / 2, 355);

  // Extremes row: R-HOD (green) left, R-LOD (red) right, BTC price centre.
  ctx.font = `24px ${MONO}`;
  ctx.fillStyle = GREEN;
  ctx.textAlign = "left";
  ctx.fillText("R-HOD", 120, 420);
  drawValue(ctx, snapshot.hodRatio, 120, 468, GREEN);
  ctx.font = `22px ${MONO}`;
  ctx.fillStyle = GREEN;
  ctx.fillText(snapshot.hodPrice, 120, 500);

  ctx.fillStyle = RED;
  ctx.textAlign = "right";
  ctx.font = `24px ${MONO}`;
  ctx.fillText("R-LOD", WIDTH - 120, 420);
  drawValue(ctx, snapshot.lodRatio, WIDTH - 120, 468, RED);
  ctx.font = `22px ${MONO}`;
  ctx.fillStyle = RED;
  ctx.fillText(snapshot.lodPrice, WIDTH - 120, 500);

  ctx.textAlign = "center";
  ctx.fillStyle = "#8a93a5";
  ctx.font = `20px ${MONO}`;
  ctx.fillText("BITCOIN", WIDTH / 2, 420);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold 40px ${MONO}`;
  ctx.fillText(snapshot.btcPrice, WIDTH / 2, 468);
  ctx.fillStyle = "#cbd5e6";
  ctx.font = `20px ${MONO}`;
  ctx.fillText(`BID ${snapshot.bidDepth}   ·   ASK ${snapshot.askDepth}`, WIDTH / 2, 500);

  // Self-dating footer -- the compliance mechanism, never optional.
  ctx.fillStyle = "#8a93a5";
  ctx.font = `22px ${MONO}`;
  ctx.fillText(footerLine(snapshot, capturedAt), WIDTH / 2, 580);
}

function loadLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/icon-192.png";
  });
}

/** Render the snapshot and hand it to the OS share sheet; download when
 * the browser can't share files. A user-cancelled share is not an error. */
export async function shareSnapshot(snapshot: ShareSnapshot): Promise<"shared" | "downloaded"> {
  const capturedAt = new Date();
  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  drawShareImage(ctx, snapshot, capturedAt, await loadLogo());
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("could not render the share image");
  const name = `bitclock-${formatCaptureUtc(capturedAt).replace(/[: ]/g, "-").replace("-UTC", "Z")}.png`;
  const file = new File([blob], name, { type: "image/png" });
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: "BitClock order book ratio" });
      return "shared";
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return "shared";
      // Fall through to the download path (e.g. share sheet unavailable in this context).
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return "downloaded";
}
